import type { HelperMessage } from "../game/help/FakeFifo";

export type Identity = { displayName: string; tag: string };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export class UiController {
  private root: HTMLElement;
  private hud: HTMLElement;
  private msgCard: HTMLElement;
  private msgFrom: HTMLElement;
  private msgBody: HTMLElement;
  private screens: Record<string, HTMLElement> = {};

  onStart?: (id: Identity) => void;
  onAcceptHelp?: () => void;
  onRefuseHelp?: () => void;
  onCompose?: (body: string) => void;
  onShowBoard?: () => void;
  onBackTitle?: () => void;
  onRetry?: () => void;

  constructor(root: HTMLElement) {
    this.root = root;
    this.hud = el("div", "hud");
    this.hud.innerHTML = `<span class="spares">Spares 3</span><span class="act">Alone</span>`;
    this.hud.classList.add("hidden");
    this.hud.style.display = "none";

    this.msgCard = el("div", "message-card");
    this.msgFrom = el("div", "from");
    this.msgBody = el("div", "body");
    this.msgCard.append(this.msgFrom, this.msgBody);

    this.root.append(this.hud, this.msgCard);
    this.buildTitle();
    this.buildHelp();
    this.buildResults();
    this.buildCompose();
    this.buildBoard();
    this.show("title");
  }

  private buildTitle(): void {
    const s = el("div", "screen");
    s.id = "screen-title";
    s.append(
      el("h1", undefined, "Ending E"),
      el(
        "p",
        "subtitle",
        "A fan museum ritual. One thumb. Auto-fire. Optional help from those who made it. Not affiliated with Square Enix.",
      ),
    );
    const nameLabel = el("label", undefined, "Display name");
    const name = el("input") as HTMLInputElement;
    name.id = "name-input";
    name.maxLength = 24;
    name.placeholder = "Visitor";
    name.setAttribute("autocomplete", "nickname");

    const tagLabel = el("label", undefined, "Tag (optional)");
    const tag = el("input") as HTMLInputElement;
    tag.id = "tag-input";
    tag.maxLength = 8;
    tag.placeholder = "00";

    const start = el("button", "btn primary", "Begin") as HTMLButtonElement;
    start.type = "button";
    start.addEventListener("click", () => {
      const displayName = name.value.trim() || "Visitor";
      const t = tag.value.trim();
      this.onStart?.({ displayName, tag: t });
    });

    const board = el("button", "btn", "Those who made it") as HTMLButtonElement;
    board.type = "button";
    board.addEventListener("click", () => this.onShowBoard?.());

    const row = el("div", "btn-row");
    row.append(start, board);
    s.append(nameLabel, name, tagLabel, tag, row);
    s.append(el("div", "fan-note", "Fan tribute · Open-license audio · Cloudflare"));
    this.root.append(s);
    this.screens.title = s;
  }

  private buildHelp(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-help";
    s.append(
      el("h1", undefined, "Connection"),
      el(
        "p",
        "subtitle",
        "Others who cleared this sky left words behind. Accept their help, or refuse and continue alone.",
      ),
    );
    const accept = el("button", "btn primary", "Accept help") as HTMLButtonElement;
    const refuse = el("button", "btn", "Refuse") as HTMLButtonElement;
    accept.type = "button";
    refuse.type = "button";
    accept.addEventListener("click", () => this.onAcceptHelp?.());
    refuse.addEventListener("click", () => this.onRefuseHelp?.());
    const row = el("div", "btn-row");
    row.append(accept, refuse);
    s.append(row);
    this.root.append(s);
    this.screens.help = s;
  }

  private buildResults(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-results";
    s.append(el("h1", undefined, "Clear"), el("p", "subtitle", ""));
    const sub = s.querySelector(".subtitle") as HTMLElement;
    sub.id = "results-sub";

    const compose = el("button", "btn primary", "Leave a message") as HTMLButtonElement;
    const board = el("button", "btn", "Those who made it") as HTMLButtonElement;
    const again = el("button", "btn", "Fly again") as HTMLButtonElement;
    compose.type = "button";
    board.type = "button";
    again.type = "button";
    compose.id = "btn-compose";
    compose.addEventListener("click", () => this.show("compose"));
    board.addEventListener("click", () => this.onShowBoard?.());
    again.addEventListener("click", () => this.onRetry?.());
    const row = el("div", "btn-row");
    row.append(compose, board, again);
    s.append(row);
    this.root.append(s);
    this.screens.results = s;
  }

  private buildCompose(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-compose";
    s.append(
      el("h1", undefined, "Message"),
      el(
        "p",
        "subtitle",
        "100–140 characters. No links. Your words enter the global help queue for everyone.",
      ),
    );
    const ta = el("textarea") as HTMLTextAreaElement;
    ta.id = "compose-body";
    ta.maxLength = 140;
    ta.placeholder = "Words for the next pilot…";
    const count = el("div", "char-count", "0 / 140");
    count.id = "compose-count";
    ta.addEventListener("input", () => {
      const n = ta.value.length;
      count.textContent = `${n} / 140`;
      count.classList.toggle("bad", n > 0 && (n < 100 || n > 140));
    });
    const submit = el("button", "btn primary", "Submit to the queue") as HTMLButtonElement;
    const back = el("button", "btn", "Back") as HTMLButtonElement;
    submit.type = "button";
    back.type = "button";
    submit.addEventListener("click", () => {
      const body = ta.value.trim();
      if (body.length < 100 || body.length > 140) return;
      if (/https?:\/\//i.test(body) || /www\./i.test(body)) return;
      this.onCompose?.(body);
    });
    back.addEventListener("click", () => this.show("results"));
    const row = el("div", "btn-row");
    row.append(submit, back);
    s.append(ta, count, row);
    this.root.append(s);
    this.screens.compose = s;
  }

  private buildBoard(): void {
    const s = el("div", "screen hidden");
    s.id = "screen-board";
    s.append(el("h1", undefined, "Those who made it"), el("p", "subtitle", "Messages from clearers. Browsing does not consume the help queue."));
    const list = el("ul", "scoreboard-list");
    list.id = "board-list";
    const back = el("button", "btn", "Back") as HTMLButtonElement;
    back.type = "button";
    back.addEventListener("click", () => this.onBackTitle?.());
    s.append(list, el("div", "btn-row"));
    (s.querySelector(".btn-row") as HTMLElement).append(back);
    this.root.append(s);
    this.screens.board = s;
  }

  show(name: keyof UiController["screens"] | "title" | "help" | "results" | "compose" | "board" | "flight"): void {
    for (const s of Object.values(this.screens)) {
      s.classList.add("hidden");
      s.style.display = "none";
    }
    if (name === "flight") {
      this.hud.style.display = "flex";
      return;
    }
    this.hud.style.display = "none";
    const s = this.screens[name];
    if (s) {
      s.classList.remove("hidden");
      s.style.display = "flex";
    }
  }

  setHud(spares: number, actName: string, progress: number): void {
    const spareEl = this.hud.querySelector(".spares");
    const actEl = this.hud.querySelector(".act");
    if (spareEl) spareEl.textContent = `Spares ${spares}`;
    if (actEl) actEl.textContent = `${actName} · ${Math.floor(progress * 100)}%`;
  }

  showHelperMessage(msg: HelperMessage): void {
    this.msgFrom.textContent =
      msg.tag != null && msg.tag !== ""
        ? `${msg.displayName} · ${msg.tag}`
        : msg.displayName;
    this.msgBody.textContent = msg.body;
    this.msgCard.classList.add("visible");
    window.setTimeout(() => this.msgCard.classList.remove("visible"), 4200);
  }

  setResults(elapsed: number, helped: boolean, deaths: number): void {
    const sub = document.getElementById("results-sub");
    if (sub) {
      const m = Math.floor(elapsed / 60);
      const s = Math.floor(elapsed % 60)
        .toString()
        .padStart(2, "0");
      sub.textContent = `${m}:${s} · ${deaths} falls · ${helped ? "Cleared with help" : "Solo clear"}`;
    }
  }

  setBoard(items: HelperMessage[]): void {
    const list = document.getElementById("board-list");
    if (!list) return;
    list.innerHTML = "";
    for (const m of items) {
      const li = el("li");
      const name = el(
        "div",
        "name",
        m.tag ? `${m.displayName} · ${m.tag}` : m.displayName,
      );
      const snip = el("div", "snip", m.body);
      li.append(name, snip);
      li.addEventListener("click", () => this.showHelperMessage(m));
      list.append(li);
    }
  }

  toast(text: string): void {
    this.msgFrom.textContent = "System";
    this.msgBody.textContent = text;
    this.msgCard.classList.add("visible");
    window.setTimeout(() => this.msgCard.classList.remove("visible"), 2800);
  }
}
